import App from './App.vue';
import { createApp } from 'vue';
import UiBottomSheet from '@/components/ui/UiBottomSheet.vue';
import UiModal from '@/components/ui/UiModal.vue';
import { YandexMetrikaService } from '@/services/yandex-metrika';
import { SentryService } from '@/services/sentry';
import { VendorAnalytics } from '@/services/vendor-analytics';
import { AuthService } from '@/services/auth';
import { NavigatorService } from './services/navigator';
import { clickOutside, onlyLetter, onlyNumber } from '@/utils/directives';
import { loaderPlugin } from '@/utils/plugins/loader';
import { notificationPlugin } from '@/utils/plugins/notification';
import { imageProxyPlugin } from '@/utils/plugins/image-proxy';
import { deliveryPlugin } from './utils/plugins/delivery';
import { createProvidersPlugin } from '@/utils/plugins/di/plugin-installer';
import { thousandSeparatorPlugin } from '@/utils/plugins/thousand-separator';
import { ageRestrictionPlugin } from './utils/plugins/age-restriction';
import router from './router';
import i18n from './utils/plugins/i18n';
import './assets/styles/styles.scss';
import { setupInterceptors } from './api/default.client';
import { analytics } from '@/utils/analytics-factory';
import { createProviders } from './utils/plugins/di';
import '@/utils/polyfills/promise-allsettled';
import { getCountryByDomain } from './utils/functions/get-country-by-domain';
import { Config, Town } from './types/interfaces/config';
import { ConfigPlugin, setConfig } from './utils/plugins/config';
import { DEFAULT_CONFIG } from './utils/constants/default-config';
import { VISIBLE_TOWNS, isAppDisabled } from './utils/constants';
import AppUnavailable from './views/AppUnavailable.vue';
import { locations } from './api';

const app = createApp(App);
app.use(loaderPlugin);

// Clear slow connection UI before loading
if (typeof window.clearSlowConnection === 'function') {
	window.clearSlowConnection();
}

const providers = createProviders(AuthService);
setupInterceptors(AuthService, SentryService, providers, analytics);

const fetchConfig = (): Promise<{ config: Config; towns: Town[] }> => {
	const countryConfig = getCountryByDomain();

	if (!countryConfig) {
		return Promise.resolve({
			config: DEFAULT_CONFIG,
			towns: VISIBLE_TOWNS
		});
	}

	return new Promise((resolve, reject) => {
		locations
			.getCountryConfig(countryConfig.id)
			.then(response => {
				resolve(response.data);
			})
			.catch(error => {
				console.error('Failed to fetch config:', error);
				reject(error);
			});
	});
};

const initApp = () => {
	app.use(router);
	app.use(i18n);
	app.use(notificationPlugin);
	app.use(imageProxyPlugin);
	app.use(createProvidersPlugin(AuthService));
	app.use(deliveryPlugin);
	app.use(thousandSeparatorPlugin);
	app.use(ageRestrictionPlugin, { i18n });

	const config = providers.application.getCustomConfig();
	if (config?.analytics) {
		VendorAnalytics.init(config.analytics);
	}

	YandexMetrikaService.init(router);
	SentryService.init(app, router);

	app.directive('click-outside', clickOutside);
	app.directive('only-number', onlyNumber);
	app.directive('only-letter', onlyLetter);

	app.component('UiBottomSheet', UiBottomSheet);
	app.component('UiModal', UiModal);

	app.mount('#app');

	// Перф-метки для нативного WebView: момент монтирования Vue (это двигает
	// non-blocking mount) и первый кадр после него. Детерминированы, иммунны
	// к редиректам/кросс-домену. В приложении читаются мостом, в браузере — no-op.
	const bridge = window.ReactNativeWebView as
		| { postMessage?: (message: string) => void }
		| undefined;
	const emitAppMark = (mark: string) =>
		bridge?.postMessage?.(
			JSON.stringify({
				event: 'perf-app',
				mark,
				t: Math.round(performance.now())
			})
		);
	emitAppMark('mounted');
	requestAnimationFrame(() =>
		requestAnimationFrame(() => emitAppMark('painted'))
	);

	// Прячем стартовый скелетон (из index.html), когда первичная навигация и её
	// data-guard'ы завершились — контент вот-вот отрисуется. Даём кадр на рендер,
	// чтобы не мигнуть пустотой. Фолбэк-таймер скрытия есть в index.html.
	router.isReady().then(hideSkeleton);
};

const hideSkeleton = () => {
	requestAnimationFrame(() =>
		requestAnimationFrame(() => {
			(window as unknown as { __hideSkeleton?: () => void }).__hideSkeleton?.();
		})
	);
};

// Приложение отключено для бренда — показываем только экран недоступности,
// без роутера, авторизации и запросов к API
const initUnavailableApp = () => {
	const unavailableApp = createApp(AppUnavailable);
	unavailableApp.use(i18n);
	unavailableApp.mount('#app');

	hideSkeleton();
};

if (isAppDisabled()) {
	initUnavailableApp();
} else {
	// Провайдим реактивный configState (сидит на DEFAULT_CONFIG) и монтируем сразу —
	// конфиг больше не блокирует первый рендер, а применяется реактивно по приходу.
	app.use(ConfigPlugin);
	initApp();

	fetchConfig()
		.then(response => {
			setConfig(response.config);
			NavigatorService.setTowns(response.towns);
		})
		.catch(error => {
			console.error('Failed to fetchconfig:', error);
		});
}
